import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Sparkles, ArrowLeft, Star, MapPin, CheckCircle2, Clock, 
  HardHat, MessageSquare, Loader2, RefreshCw, Brain
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIMatchingPage = () => {
  const { t } = useTranslation();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchJobAndRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch job details
      const jobRes = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
      setJob(jobRes.data);

      // Fetch AI recommendations
      const recRes = await axios.get(`${API}/jobs/${jobId}/recommendations?limit=5`, { withCredentials: true });
      setRecommendations(recRes.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des recommandations');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobAndRecommendations();
  }, [fetchJobAndRecommendations]);

  const refreshRecommendations = async () => {
    setRefreshing(true);
    try {
      const recRes = await axios.get(`${API}/jobs/${jobId}/recommendations?limit=5`, { withCredentials: true });
      setRecommendations(recRes.data.recommendations || []);
      toast.success('Recommandations mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Brain className="w-8 h-8 text-[#1E3A8A] animate-pulse" />
          </div>
          <p className="text-[#4B5563]">Analyse IA en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8 text-[#F59E0B]" />
            <h1 className="font-heading font-bold text-3xl text-[#111827]">
              Matching IA
            </h1>
          </div>
          <p className="text-[#4B5563]">
            Recommandations intelligentes de sous-traitants pour votre chantier
          </p>
        </div>

        {/* Job Summary */}
        {job && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className="badge-trade mb-2">{t(`trades.${job.trade_category}`) || job.trade_category}</Badge>
                  <h2 className="font-heading font-bold text-xl text-[#111827]">{job.title}</h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#4B5563]">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.city}, {job.country}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {job.duration}
                    </span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={refreshRecommendations}
                  disabled={refreshing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#1E3A8A]" />
            Top {recommendations.length} sous-traitants recommandés
          </h3>

          {recommendations.length > 0 ? (
            recommendations.map((contractor, index) => (
              <Card key={contractor.id} className="card-surface" data-testid={`recommendation-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    {/* Rank Badge */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-[#F59E0B] text-white' : 
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-[#1E3A8A]/10 text-[#1E3A8A]'
                    }`}>
                      <span className="font-bold text-lg">#{index + 1}</span>
                    </div>

                    {/* Contractor Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-heading font-bold text-lg">{contractor.company_name}</h4>
                        {contractor.is_verified && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        )}
                      </div>

                      <div className="flex items-center gap-4 mb-3 text-sm">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                          <span className="font-medium">{contractor.rating?.toFixed(1) || '0.0'}</span>
                          <span className="text-[#4B5563]">({contractor.review_count || 0} avis)</span>
                        </span>
                        <span className="flex items-center gap-1 text-[#4B5563]">
                          <MapPin className="w-4 h-4" />
                          {contractor.city}, {contractor.country}
                        </span>
                        {contractor.years_experience > 0 && (
                          <span className="text-[#4B5563]">
                            {contractor.years_experience} ans exp.
                          </span>
                        )}
                      </div>

                      {/* Match Score */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Score de compatibilité</span>
                          <span className="text-sm font-bold text-[#1E3A8A]">{contractor.match_score}%</span>
                        </div>
                        <Progress value={contractor.match_score} className="h-2" />
                      </div>

                      {/* Match Reasons */}
                      {contractor.match_reasons && contractor.match_reasons.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {contractor.match_reasons.map((reason, i) => (
                            <Badge key={`${reason}-${i}`} variant="secondary" className="text-xs">
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Trades */}
                      <div className="flex flex-wrap gap-1 mb-4">
                        {contractor.trades?.slice(0, 4).map((trade, i) => (
                          <Badge key={trade || i} className="badge-trade text-xs">
                            {t(`trades.${trade}`) || trade}
                          </Badge>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Link to={`/contractors/${contractor.id}`}>
                          <Button variant="outline" size="sm">
                            <HardHat className="w-4 h-4 mr-2" />
                            Voir le profil
                          </Button>
                        </Link>
                        <Link to={`/dashboard/messages/${contractor.id}`}>
                          <Button className="btn-accent" size="sm">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Contacter
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Sparkles className="w-12 h-12 text-[#1E3A8A]/30 mx-auto mb-4" />
                <p className="text-[#4B5563] mb-4">
                  Aucune recommandation disponible pour le moment. 
                  Assurez-vous que des sous-traitants sont inscrits avec les compétences requises.
                </p>
                <Link to="/contractors">
                  <Button variant="outline">
                    Voir tous les sous-traitants
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMatchingPage;
